/**
 * The condition line — one interpreted sentence, worst thing first.
 *
 * *"Bleeding badly. Dehydrated. Exhausted."* The panel below it is unchanged and complete;
 * this is the line that answers **"how am I?"** without the player having to read eleven
 * numbers and rank them in their head. The ranking rules are the work here, and they are
 * small — there are only three of them worth arguing about:
 *
 * 1. **An auto-derived status effect is never a clause.** `StatusEffectSource` names the vital
 * each derived effect came from — `high_thirst`, `low_health` — so `high_thirst` and the
 * `dehydrated` thirst band are the same fact arriving twice, and the seeded character has
 * exactly that pair. Only `manual` effects (infection, poison, radiation) say something the
 * four vitals do not. Without this rule the fixture's line reads *"Dehydrated. Dehydrated.
 * Infection."*, which is how an interpreted line loses the player's trust in one render.
 * 2. **Only the worst injury.** Three cuts do not make a longer sentence, they make an
 * unreadable one; the injuries section lists all of them a few centimetres below.
 * 3. **Three clauses, hard.** Past three it is not a line, it is the panel restated. The
 * ranking decides which three, which is the only reason the ranking exists.
 *
 * The scores below are an ordering, not a model of anything. What they encode is one real
 * asymmetry from `rules.py` — **thirst outranks hunger at every band**, because dehydration
 * kills in about 48 hours and starvation takes about a week, so at equal severity the thirsty
 * character is the one in more trouble.
 */
import type { CharacterSnapshot, Injury, InjurySeverity, VitalBand } from '@/api/generated';
import { VITAL_NAMES } from '@/features/character/vitals';
import type { Translate } from '@/i18n/t';

export interface ConditionClause {
  text: string;
  /** Higher is worse. Exposed so the panel can style the leading clause by how bad it is. */
  score: number;
}

/**
 * Zero means "comfortable, do not mention it" — U1 gives those bands no `note` for the same
 * reason (`quest/api/display.py`: a note under every bar is a note nobody reads).
 */
const BAND_SCORE: Record<VitalBand, number> = {
  dead: 1000,
  critically_wounded: 90,
  dehydrated: 82,
  starving: 80,
  exhausted: 70,
  badly_wounded: 60,
  thirsty: 42,
  very_hungry: 40,
  fatigued: 35,
  wounded: 30,
  tired: 20,

  healthy: 0,
  rested: 0,
  fed: 0,
  hydrated: 0,
};

/** Sits between a moderate and a severe injury: an infection is not a scratch and not a leg. */
const MANUAL_EFFECT_SCORE = 50;

const INJURY_SCORE: Record<InjurySeverity, number> = {
  severe: 75,
  moderate: 45,
  minor: 20,
};

const MAX_CLAUSES = 3;

/**
 * The clauses, worst first. Empty means nothing is wrong — callers render
 * `ui.panel.condition_steady` rather than an empty line, and the distinction is theirs to
 * make because "nothing is wrong" is a sentence and this function returns facts.
 */
export function conditionClauses(t: Translate, character: CharacterSnapshot): ConditionClause[] {
  // Death is not the worst item in a list, it is the end of the list. A dead character with a
  // bruised rib and an infection should read "Dead.", not "Dead. Infection. Bruising, ribs."
  if (character.vitals.health.band === 'dead') {
    return [{ text: character.vitals.health.band_label, score: BAND_SCORE.dead }];
  }

  const clauses: ConditionClause[] = [];

  for (const name of VITAL_NAMES) {
    const vital = character.vitals[name];
    const score = BAND_SCORE[vital.band];
    // The label is the server's (U1), localized, and already the word the bar is showing.
    if (score > 0) clauses.push({ text: vital.band_label, score });
  }

  const worst = worstInjury(character.injuries);
  if (worst) clauses.push({ text: injuryClause(t, worst), score: INJURY_SCORE[worst.severity] });

  for (const effect of character.status_effects) {
    if (effect.source !== 'manual') continue;
    clauses.push({ text: effect.display_name, score: MANUAL_EFFECT_SCORE });
  }

  return clauses.sort(byScoreThenText).slice(0, MAX_CLAUSES);
}

/** The whole line, punctuated. No clauses is the healthy case, spelled out. */
export function conditionLine(t: Translate, character: CharacterSnapshot): string {
  const clauses = conditionClauses(t, character);
  if (clauses.length === 0) return t('ui.panel.condition_steady');
  return clauses.map((clause) => `${clause.text}.`).join(' ');
}

/**
 * *"Moderate laceration, left forearm"* — severity, then what it is, then where.
 *
 * Every slot is already in the story's language. `location` is free text the DM wrote (L8,
 * which is when `add_injury` stopped asking for `'left_thigh'`) and `severity` is an engine
 * enum joined from `ui.injury.severity.*`. **`name` is the one that took two goes**: the
 * engine substring-matches `type` to keep deprivation injuries from stacking
 * (`rules.starvation_effects`), so it is an identifier and cannot be rendered — this used to
 * print it anyway and a Ukrainian panel read *"Помірне laceration"*. `add_injury` asks for a
 * name beside the key now, and the backend joins the four it raises itself (L15); `type` is
 * still on the wire, and is still not for reading.
 *
 * Severity leads because it is the part that is ranked, and a line whose clauses start with
 * the thing they were sorted on is a line that can be skimmed — but the order lives in the
 * template, so a locale that wants the adjective after the noun can say so without touching
 * this function. German and Ukrainian both do: they put the severity in front of a noun the
 * *bundle* owns (`Verletzung`, `поранення`) and the DM's name after a colon, because nothing
 * here can decline a phrase a model wrote.
 */
export function injuryClause(t: Translate, injury: Injury): string {
  return t('ui.injury.clause', {
    severity: t(`ui.injury.severity.${injury.severity}`),
    name: injury.display_name,
    location: injury.location,
  });
}

export function worstInjury(injuries: readonly Injury[]): Injury | undefined {
  return injuries.reduce<Injury | undefined>((worst, injury) => {
    if (!worst) return injury;
    return INJURY_SCORE[injury.severity] > INJURY_SCORE[worst.severity] ? injury : worst;
  }, undefined);
}

/**
 * Ties broken on the text, not left to sort stability, so the line does not reorder itself
 * between two renders of the same character — two manual effects score identically and the
 * player would see them swap on a refetch.
 */
function byScoreThenText(a: ConditionClause, b: ConditionClause): number {
  return b.score - a.score || a.text.localeCompare(b.text);
}
